import React from 'react'
import { Outlet } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { useUnmount } from 'react-use'

import { Auth0Provider } from '@auth0/auth0-react'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { AbortControllerProvider } from '@harvey-frontend/providers/abort-controller-provider'
import { initDatadog, setDatadogState } from '@harvey-frontend/utils/datadog'
import { displayErrorMessage } from '@harvey-frontend/utils/toast'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AnimatePresence } from 'framer-motion'

import { AuthContext as UserInfoContext } from '@harvey-frontend/components/common/auth-context'
import { AnalyticsProvider } from 'components/common/analytics/analytics-context'
import { TooltipProvider } from 'components/ui/tooltip'

import Landing from '../../atoms/Landing'
import {
  DialogStatus,
  REACT_APP_AUTH_DOMAIN,
  REACT_APP_AUTH_CLIENT_ID,
  AUTH0_CACHE_LOCATION,
  AUTH_DIALOG_URL,
  REACT_APP_AUTH_AUDIENCE,
} from '../../utils/constants'
import {
  useAccessTokenContext,
  AccessTokenProvider,
} from './providers/AccessTokenProvider'
import { useDialogContext, DialogProvider } from './providers/DialogProvider'
import { navigateDialog } from './utils/office'

const queryClient = new QueryClient()
const DATADOG_SERVICE = 'harvey-for-word'
initDatadog(DATADOG_SERVICE)

if (process.env.NODE_ENV === 'development') {
  OfficeExtension.config.extendedErrorLogging = true
}

interface AppProps {
  title: string
  platform: Office.PlatformType
}
const initializePlatform = (platform: Office.PlatformType) => {
  // Word-specific overrides to the global initialization.
  datadogRum.setGlobalContextProperty('platform', platform)
  datadogLogs.setGlobalContextProperty('platform', platform)
  Sentry.setTag('platform', platform)
}

// These providers are dependant on the AccessTokenProvider, as well as each other.
// Wrapping logic here allows us to have <App /> be a pure outlet component that only
// defines the providers for every other component to use.
const UserInfoAndAnalyticsProviders = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { closeDialog } = useDialogContext()
  const {
    isAuthenticated,
    isAuthDialogOpen,
    isLoadingAccessToken,
    userInfo,
    setIsAuthDialogOpen,
    setIsLoadingAccessToken,
    handleExistingAuth,
    updateAccessToken,
  } = useAccessTokenContext()
  Sentry.setUser({ username: userInfo?.nonDbId })
  setDatadogState(userInfo)

  const openAuthDialog = () => {
    setIsAuthDialogOpen(true)
    navigateDialog(
      AUTH_DIALOG_URL,
      async (status: DialogStatus, parsedAccessToken?: string) => {
        if (status === DialogStatus.success) {
          setIsLoadingAccessToken(true)
          try {
            await handleExistingAuth()
          } catch (e) {
            Sentry.captureException(e)
            if (parsedAccessToken) {
              await updateAccessToken(parsedAccessToken)
            } else {
              displayErrorMessage('Error fetching access token')
            }
          }
          setIsLoadingAccessToken(false)
        }
        setIsAuthDialogOpen(false)
      }
    )
  }
  useUnmount(() => {
    closeDialog()
  })
  if (!isAuthenticated) {
    return (
      <Landing
        isButtonDisabled={isAuthDialogOpen || isLoadingAccessToken}
        openAuthDialog={openAuthDialog}
      />
    )
  }
  return (
    <UserInfoContext.Provider value={userInfo ? { userInfo } : null}>
      <AnalyticsProvider>{children}</AnalyticsProvider>
    </UserInfoContext.Provider>
  )
}

const App = (props: AppProps) => {
  initializePlatform(props.platform)
  const location = useLocation()

  return (
    <Auth0Provider
      domain={REACT_APP_AUTH_DOMAIN ?? ''}
      clientId={REACT_APP_AUTH_CLIENT_ID ?? ''}
      cacheLocation={AUTH0_CACHE_LOCATION}
      audience={REACT_APP_AUTH_AUDIENCE ?? ''}
    >
      <TooltipProvider>
        <AccessTokenProvider>
          <AbortControllerProvider>
            <DialogProvider>
              <UserInfoAndAnalyticsProviders>
                <QueryClientProvider client={queryClient}>
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  <AnimatePresence mode="wait" initial={false}>
                    <Outlet key={location.pathname} />
                  </AnimatePresence>
                </QueryClientProvider>
              </UserInfoAndAnalyticsProviders>
            </DialogProvider>
          </AbortControllerProvider>
        </AccessTokenProvider>
      </TooltipProvider>
    </Auth0Provider>
  )
}

export default App
